import { Directive, ElementRef, HostBinding, inject, Input } from '@angular/core';

@Directive({
  selector: '[cslButton]',
  standalone: true,
})
export class ButtonDirective {
  private readonly elementRef: ElementRef<HTMLButtonElement> = inject(ElementRef);
  @Input({
    alias: 'cslButton',
    // eslint-disable-next-line @angular-eslint/no-input-rename
    transform: (v: ButtonType | '') => v || 'primary',
  })
  type!: ButtonType;
  @Input() size: 'default' | 'lg' = 'default';
  @Input() inverted = false;

  @HostBinding('class') get buttonClass(): string {
    const baseStyles = `flex items-center justify-center gap-2 rounded-full text-base font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
      this.inverted ? 'focus-visible:outline-white' : 'focus-visible:outline-focus'
    }`;
    let addStyles = 'cursor-pointer text-gray-950';
    let disabledStyles = '';

    switch (this.size) {
      case 'default': {
        addStyles = `${addStyles} px-6 py-3`;
        break;
      }
      case 'lg': {
        addStyles = `${addStyles} px-8 py-4`;
        break;
      }
    }

    switch (this.type) {
      case 'primary':
      case 'primary-icon': {
        addStyles = `${addStyles} bg-green-600 hover:bg-green-400 active:bg-green-700`;
        disabledStyles = `${disabledStyles} bg-gray-500 text-gray-800`;
        break;
      }
      case 'secondary':
      case 'secondary-icon': {
        addStyles = `${addStyles} border border-blue-800 hover:bg-gray-100 active:bg-gray-200`;
        disabledStyles = `${disabledStyles} border text-neutral-300 border-neutral-300`;
        break;
      }
      case 'primary-link': {
        addStyles = `${addStyles} text-green-600 hover:text-green-400 underline active:text-green-700 active:no-underline`;
        disabledStyles = `${disabledStyles} text-gray-600`;
        break;
      }
      case 'secondary-link': {
        addStyles = `${addStyles} text-blue-900 hover:text-blue-800 underline active:text-blue-800 active:no-underline`;
        disabledStyles = `${disabledStyles} text-gray-500`;
        break;
      }
      case 'default': {
        addStyles = 'cursor-pointer';
        break;
      }
    }

    switch (this.type) {
      case 'primary-icon':
      case 'secondary-icon': {
        addStyles = `${addStyles} w-0 h-14`;
        break;
      }
    }
    return `${baseStyles} ${this.elementRef.nativeElement.disabled ? disabledStyles : addStyles}`;
  }
}

export type ButtonType =
  'primary' | 'secondary' | 'primary-icon' | 'secondary-icon' | 'primary-link' | 'secondary-link' | 'default';
